import type { ThemeMode } from '@/theme/themes';

export type PlanningThemeVariant = 'default' | 'henna' | 'wedding';

const defaultLightPlanningTheme = `
      --planning-ink: #181421;
      --planning-muted: #50475f;
      --planning-soft: #6a6077;
      --planning-heading: #201733;
      --planning-card: rgba(255, 255, 255, 0.82);
      --planning-card-strong: rgba(255, 255, 255, 0.9);
      --planning-card-hover: rgba(112, 83, 184, 0.12);
      --planning-panel: rgba(255, 255, 255, 0.72);
      --planning-panel-strong: rgba(255, 255, 255, 0.88);
      --planning-border: rgba(112, 83, 184, 0.28);
      --planning-border-muted: rgba(112, 83, 184, 0.18);
      --planning-border-strong: rgba(112, 83, 184, 0.48);
      --planning-accent: #7053b8;
      --planning-accent-soft: #523985;
      --planning-accent-deep: #7053b8;
      --planning-lavender: #7053b8;
      --planning-on-accent: #ffffff;
      --planning-button-bg: rgba(112, 83, 184, 0.08);
      --planning-button-hover-bg: #7053b8;
      --planning-button-hover-ink: #ffffff;
      --planning-shadow: rgba(70, 54, 116, 0.12);
      --planning-shadow-strong: rgba(70, 54, 116, 0.18);
      --planning-header-bg: rgba(250, 251, 255, 0.9);
      --planning-header-border: rgba(112, 83, 184, 0.22);
      --planning-header-shadow: 0 14px 34px rgba(70, 54, 116, 0.14);
      --planning-menu-ink: #3f3752;
      --planning-menu-muted: #5f546e;
      --planning-menu-active-ink: #4c347a;
      --planning-menu-active-bg: rgba(112, 83, 184, 0.14);
      --planning-menu-hover-bg: rgba(112, 83, 184, 0.1);
      --planning-control-bg: rgba(255, 255, 255, 0.74);
      --planning-control-border: rgba(112, 83, 184, 0.36);
      --planning-visual-glow: rgba(112, 83, 184, 0.18);
      --planning-visual-glow-soft: rgba(112, 83, 184, 0.1);
      --planning-visual-bg-start: rgba(255, 255, 255, 0.92);
      --planning-visual-bg-mid: rgba(238, 233, 255, 0.74);
      --planning-visual-bg-end: rgba(244, 246, 251, 0.96);
      --planning-page-bg: linear-gradient(135deg, #f7f8fc 0%, #edf0f8 52%, #f4efff 100%);
`;

const defaultDarkPlanningTheme = `
      --planning-ink: #ffffff;
      --planning-muted: #e2dcec;
      --planning-soft: #cfc5df;
      --planning-heading: #ffffff;
      --planning-card: rgba(18, 19, 26, 0.36);
      --planning-card-strong: rgba(18, 19, 26, 0.56);
      --planning-card-hover: rgba(184, 151, 255, 0.12);
      --planning-panel: rgba(18, 19, 26, 0.28);
      --planning-panel-strong: rgba(18, 19, 26, 0.64);
      --planning-border: rgba(184, 151, 255, 0.44);
      --planning-border-muted: rgba(184, 151, 255, 0.22);
      --planning-border-strong: rgba(216, 199, 255, 0.66);
      --planning-accent: #b897ff;
      --planning-accent-soft: #d8c7ff;
      --planning-accent-deep: #f3ecff;
      --planning-lavender: #a98dff;
      --planning-on-accent: #12131a;
      --planning-button-bg: rgba(216, 199, 255, 0.08);
      --planning-button-hover-bg: #f3ecff;
      --planning-button-hover-ink: #12131a;
      --planning-shadow: rgba(8, 7, 13, 0.18);
      --planning-shadow-strong: rgba(0, 0, 0, 0.34);
      --planning-header-bg: rgba(18, 19, 26, 0.84);
      --planning-header-border: rgba(184, 151, 255, 0.28);
      --planning-header-shadow: 0 14px 34px rgba(0, 0, 0, 0.34);
      --planning-menu-ink: #f6f0ff;
      --planning-menu-muted: #e2dcec;
      --planning-menu-active-ink: #f3ecff;
      --planning-menu-active-bg: rgba(184, 151, 255, 0.16);
      --planning-menu-hover-bg: rgba(216, 199, 255, 0.12);
      --planning-control-bg: rgba(18, 19, 26, 0.34);
      --planning-control-border: rgba(184, 151, 255, 0.48);
      --planning-visual-glow: rgba(216, 199, 255, 0.3);
      --planning-visual-glow-soft: rgba(216, 199, 255, 0.12);
      --planning-visual-bg-start: rgba(18, 19, 26, 0.9);
      --planning-visual-bg-mid: rgba(50, 38, 78, 0.6);
      --planning-visual-bg-end: rgba(18, 19, 26, 0.92);
      --planning-page-bg: linear-gradient(135deg, #12131a 0%, #202135 48%, #171b2a 100%);
`;

const hennaLightPlanningTheme = `
      --planning-ink: #2a0808;
      --planning-muted: #5f3020;
      --planning-soft: #70401f;
      --planning-heading: #2a0808;
      --planning-card: rgba(255, 250, 240, 0.84);
      --planning-card-strong: rgba(255, 248, 222, 0.92);
      --planning-card-hover: rgba(214, 168, 66, 0.18);
      --planning-panel: rgba(255, 246, 223, 0.76);
      --planning-panel-strong: rgba(255, 248, 222, 0.9);
      --planning-border: rgba(163, 22, 22, 0.28);
      --planning-border-muted: rgba(184, 135, 31, 0.22);
      --planning-border-strong: rgba(184, 135, 31, 0.58);
      --planning-accent: #a31616;
      --planning-accent-soft: #7d1212;
      --planning-accent-deep: #a31616;
      --planning-lavender: #b8871f;
      --planning-on-accent: #fffaf0;
      --planning-button-bg: rgba(163, 22, 22, 0.08);
      --planning-button-hover-bg: #a31616;
      --planning-button-hover-ink: #fffaf0;
      --planning-shadow: rgba(105, 35, 16, 0.14);
      --planning-shadow-strong: rgba(105, 35, 16, 0.22);
      --planning-header-bg: rgba(255, 246, 223, 0.9);
      --planning-header-border: rgba(163, 22, 22, 0.24);
      --planning-header-shadow: 0 14px 34px rgba(105, 35, 16, 0.16);
      --planning-menu-ink: #3a130c;
      --planning-menu-muted: #6d3a22;
      --planning-menu-active-ink: #7d1212;
      --planning-menu-active-bg: rgba(214, 168, 66, 0.2);
      --planning-menu-hover-bg: rgba(163, 22, 22, 0.1);
      --planning-control-bg: rgba(255, 250, 240, 0.76);
      --planning-control-border: rgba(184, 135, 31, 0.42);
      --planning-visual-glow: rgba(214, 168, 66, 0.28);
      --planning-visual-glow-soft: rgba(163, 22, 22, 0.08);
      --planning-visual-bg-start: rgba(255, 250, 240, 0.94);
      --planning-visual-bg-mid: rgba(255, 246, 223, 0.78);
      --planning-visual-bg-end: rgba(255, 232, 194, 0.96);
      --planning-page-bg: linear-gradient(135deg, #fffaf0 0%, #fff6df 52%, #ffe8c2 100%);
`;

const hennaDarkPlanningTheme = `
      --planning-ink: #fff4d8;
      --planning-muted: #eadcae;
      --planning-soft: #d9c683;
      --planning-heading: #fff4d8;
      --planning-card: rgba(2, 7, 19, 0.52);
      --planning-card-strong: rgba(6, 20, 42, 0.7);
      --planning-card-hover: rgba(241, 207, 122, 0.14);
      --planning-panel: rgba(2, 7, 19, 0.42);
      --planning-panel-strong: rgba(6, 20, 42, 0.76);
      --planning-border: rgba(216, 169, 67, 0.44);
      --planning-border-muted: rgba(216, 169, 67, 0.24);
      --planning-border-strong: rgba(241, 207, 122, 0.66);
      --planning-accent: #d8a943;
      --planning-accent-soft: #f1cf7a;
      --planning-accent-deep: #fff6d9;
      --planning-lavender: #d8a943;
      --planning-on-accent: #06142a;
      --planning-button-bg: rgba(241, 207, 122, 0.1);
      --planning-button-hover-bg: #f1cf7a;
      --planning-button-hover-ink: #06142a;
      --planning-shadow: rgba(0, 0, 0, 0.2);
      --planning-shadow-strong: rgba(0, 0, 0, 0.44);
      --planning-header-bg: rgba(2, 7, 19, 0.86);
      --planning-header-border: rgba(216, 169, 67, 0.32);
      --planning-header-shadow: 0 14px 34px rgba(0, 0, 0, 0.38);
      --planning-menu-ink: #fff4d8;
      --planning-menu-muted: #eadcae;
      --planning-menu-active-ink: #f1cf7a;
      --planning-menu-active-bg: rgba(241, 207, 122, 0.16);
      --planning-menu-hover-bg: rgba(241, 207, 122, 0.12);
      --planning-control-bg: rgba(6, 20, 42, 0.56);
      --planning-control-border: rgba(241, 207, 122, 0.5);
      --planning-visual-glow: rgba(241, 207, 122, 0.3);
      --planning-visual-glow-soft: rgba(23, 74, 134, 0.22);
      --planning-visual-bg-start: rgba(2, 7, 19, 0.92);
      --planning-visual-bg-mid: rgba(7, 26, 51, 0.72);
      --planning-visual-bg-end: rgba(44, 31, 12, 0.92);
      --planning-page-bg: linear-gradient(135deg, #020713 0%, #071a33 50%, #2f1a0c 100%);
`;

const weddingLightPlanningTheme = `
      --planning-ink: #2b0d08;
      --planning-muted: #5c3126;
      --planning-soft: #704230;
      --planning-heading: #2b0d08;
      --planning-card: rgba(255, 243, 238, 0.84);
      --planning-card-strong: rgba(255, 247, 237, 0.92);
      --planning-card-hover: rgba(209, 160, 72, 0.18);
      --planning-panel: rgba(255, 240, 232, 0.76);
      --planning-panel-strong: rgba(255, 247, 237, 0.9);
      --planning-border: rgba(184, 90, 72, 0.3);
      --planning-border-muted: rgba(185, 131, 47, 0.22);
      --planning-border-strong: rgba(185, 131, 47, 0.58);
      --planning-accent: #9f3e32;
      --planning-accent-soft: #7f2d28;
      --planning-accent-deep: #9f3e32;
      --planning-lavender: #b9832f;
      --planning-on-accent: #fff3ee;
      --planning-button-bg: rgba(159, 62, 50, 0.08);
      --planning-button-hover-bg: #9f3e32;
      --planning-button-hover-ink: #fff3ee;
      --planning-shadow: rgba(105, 44, 28, 0.14);
      --planning-shadow-strong: rgba(105, 44, 28, 0.22);
      --planning-header-bg: rgba(255, 240, 232, 0.9);
      --planning-header-border: rgba(184, 90, 72, 0.24);
      --planning-header-shadow: 0 14px 34px rgba(105, 44, 28, 0.16);
      --planning-menu-ink: #3a160f;
      --planning-menu-muted: #6b3b2d;
      --planning-menu-active-ink: #7f2d28;
      --planning-menu-active-bg: rgba(209, 160, 72, 0.2);
      --planning-menu-hover-bg: rgba(159, 62, 50, 0.1);
      --planning-control-bg: rgba(255, 243, 238, 0.76);
      --planning-control-border: rgba(185, 131, 47, 0.42);
      --planning-visual-glow: rgba(209, 160, 72, 0.28);
      --planning-visual-glow-soft: rgba(159, 62, 50, 0.08);
      --planning-visual-bg-start: rgba(255, 243, 238, 0.94);
      --planning-visual-bg-mid: rgba(255, 240, 232, 0.78);
      --planning-visual-bg-end: rgba(255, 227, 209, 0.96);
      --planning-page-bg: linear-gradient(135deg, #fff3ee 0%, #fff0e8 52%, #ffe3d1 100%);
`;

const weddingDarkPlanningTheme = `
      --planning-ink: #fff7df;
      --planning-muted: #e8d8ae;
      --planning-soft: #d9c489;
      --planning-heading: #fff7df;
      --planning-card: rgba(5, 4, 3, 0.52);
      --planning-card-strong: rgba(9, 7, 5, 0.72);
      --planning-card-hover: rgba(244, 213, 123, 0.14);
      --planning-panel: rgba(5, 4, 3, 0.44);
      --planning-panel-strong: rgba(9, 7, 5, 0.78);
      --planning-border: rgba(210, 163, 59, 0.44);
      --planning-border-muted: rgba(210, 163, 59, 0.24);
      --planning-border-strong: rgba(244, 213, 123, 0.66);
      --planning-accent: #d2a33b;
      --planning-accent-soft: #f4d57b;
      --planning-accent-deep: #fff4c5;
      --planning-lavender: #d2a33b;
      --planning-on-accent: #090705;
      --planning-button-bg: rgba(244, 213, 123, 0.1);
      --planning-button-hover-bg: #f4d57b;
      --planning-button-hover-ink: #090705;
      --planning-shadow: rgba(0, 0, 0, 0.2);
      --planning-shadow-strong: rgba(0, 0, 0, 0.44);
      --planning-header-bg: rgba(5, 4, 3, 0.88);
      --planning-header-border: rgba(210, 163, 59, 0.32);
      --planning-header-shadow: 0 14px 34px rgba(0, 0, 0, 0.38);
      --planning-menu-ink: #fff7df;
      --planning-menu-muted: #e8d8ae;
      --planning-menu-active-ink: #f4d57b;
      --planning-menu-active-bg: rgba(244, 213, 123, 0.16);
      --planning-menu-hover-bg: rgba(244, 213, 123, 0.12);
      --planning-control-bg: rgba(9, 7, 5, 0.58);
      --planning-control-border: rgba(244, 213, 123, 0.5);
      --planning-visual-glow: rgba(244, 213, 123, 0.3);
      --planning-visual-glow-soft: rgba(210, 163, 59, 0.16);
      --planning-visual-bg-start: rgba(5, 4, 3, 0.92);
      --planning-visual-bg-mid: rgba(9, 7, 5, 0.74);
      --planning-visual-bg-end: rgba(42, 25, 8, 0.92);
      --planning-page-bg: linear-gradient(135deg, #050403 0%, #070604 48%, #2a1908 100%);
`;

const scrollThemes = {
  default: {
    light: {
      color: '#7053b8 #f4f6fb',
      track: '#f4f6fb',
      thumb: 'linear-gradient(180deg, #9b83dc 0%, #7053b8 100%)',
      thumbHover: 'linear-gradient(180deg, #7053b8 0%, #4c347a 100%)',
    },
    dark: {
      color: '#b897ff #12131a',
      track: '#12131a',
      thumb: 'linear-gradient(180deg, #d8c7ff 0%, #8f6dff 100%)',
      thumbHover: 'linear-gradient(180deg, #f0e8ff 0%, #b897ff 100%)',
    },
  },
  henna: {
    light: {
      color: '#a31616 #fffaf0',
      track: '#fffaf0',
      thumb: 'linear-gradient(180deg, #d6a842 0%, #a31616 100%)',
      thumbHover: 'linear-gradient(180deg, #b8871f 0%, #7d1212 100%)',
    },
    dark: {
      color: '#f1cf7a #020713',
      track: '#020713',
      thumb: 'linear-gradient(180deg, #f1cf7a 0%, #174a86 100%)',
      thumbHover: 'linear-gradient(180deg, #fff6d9 0%, #d8a943 100%)',
    },
  },
  wedding: {
    light: {
      color: '#9f3e32 #fff3ee',
      track: '#fff3ee',
      thumb: 'linear-gradient(180deg, #d1a048 0%, #9f3e32 100%)',
      thumbHover: 'linear-gradient(180deg, #b9832f 0%, #7f2d28 100%)',
    },
    dark: {
      color: '#f4d57b #050403',
      track: '#050403',
      thumb: 'linear-gradient(180deg, #f4d57b 0%, #d2a33b 100%)',
      thumbHover: 'linear-gradient(180deg, #fff4c5 0%, #d2a33b 100%)',
    },
  },
} as const;

const pageBackgrounds = {
  default: {
    light: '#f4f6fb',
    dark: '#12131a',
  },
  henna: {
    light: '#fffaf0',
    dark: '#020713',
  },
  wedding: {
    light: '#fff3ee',
    dark: '#050403',
  },
} as const;

const planningThemes = {
  default: {
    light: defaultLightPlanningTheme,
    dark: defaultDarkPlanningTheme,
  },
  henna: {
    light: hennaLightPlanningTheme,
    dark: hennaDarkPlanningTheme,
  },
  wedding: {
    light: weddingLightPlanningTheme,
    dark: weddingDarkPlanningTheme,
  },
} as const;

const normalizePlanningThemeVariant = (
  variant: PlanningThemeVariant | undefined,
) => variant ?? 'default';

export const getPlanningPageBackground = (
  mode: ThemeMode,
  variant?: PlanningThemeVariant,
) => pageBackgrounds[normalizePlanningThemeVariant(variant)][mode];

export const getPlanningPageTheme = (
  mode: ThemeMode,
  variant?: PlanningThemeVariant,
) => planningThemes[normalizePlanningThemeVariant(variant)][mode];

export const getPlanningScrollbarColor = (
  mode: ThemeMode,
  variant?: PlanningThemeVariant,
) => scrollThemes[normalizePlanningThemeVariant(variant)][mode].color;

export const getPlanningScrollbarTrack = (
  mode: ThemeMode,
  variant?: PlanningThemeVariant,
) => scrollThemes[normalizePlanningThemeVariant(variant)][mode].track;

export const getPlanningScrollbarThumb = (
  mode: ThemeMode,
  variant?: PlanningThemeVariant,
) => scrollThemes[normalizePlanningThemeVariant(variant)][mode].thumb;

export const getPlanningScrollbarThumbHover = (
  mode: ThemeMode,
  variant?: PlanningThemeVariant,
) => scrollThemes[normalizePlanningThemeVariant(variant)][mode].thumbHover;
